// Typed mock datasets for Procurement V2. Centralised here so the thin service
// layer (procurementV2.js) stays swappable: when the real endpoints land, delete
// these generators and point the queries at the API. Shapes mirror the intended
// API responses.

const CENTERS = [
  { code: "HN-01", name: "HN-01 Cau Giay", region: "North" },
  { code: "HN-02", name: "HN-02 Ha Dong", region: "North" },
  { code: "HCM-01", name: "HCM-01 District 1", region: "South" },
  { code: "HCM-02", name: "HCM-02 Thu Duc", region: "South" },
  { code: "DN-01", name: "DN-01 Hai Chau", region: "Central" },
]

const CATEGORIES = [
  { code: "it_equipment", name: "IT Equipment" },
  { code: "office_supplies", name: "Office Supplies" },
  { code: "facilities", name: "Facilities & Maintenance" },
  { code: "marketing", name: "Marketing & Events" },
  { code: "teaching_materials", name: "Teaching Materials" },
  { code: "services", name: "Professional Services" },
]

const DEPARTMENTS = [
  { code: "operations", name: "Operations" },
  { code: "academic", name: "Academic" },
  { code: "marketing", name: "Marketing" },
  { code: "facilities", name: "Facilities" },
  { code: "it", name: "IT" },
]

const REQUESTERS = [
  { id: "me", name: "You" },
  { id: "u2", name: "Nguyen Van A" },
  { id: "u3", name: "Tran Thi B" },
  { id: "u4", name: "Le Van C" },
]

const ORDER_STATUSES = [
  "new",
  "pending",
  "info_requested",
  "approved",
  "closed",
  "rejected",
]
const STAGES = ["order", "purchase", "goods_receipt", "payment"]
const URGENCIES = ["normal", "urgent"]

const pick = (arr, i) => arr[i % arr.length]
const amt = (i) => (5 + (i % 12)) * 2500000 + (i % 5) * 750000

// ── Order Requests ───────────────────────────────────────────────────────────
const buildOrders = () =>
  Array.from({ length: 24 }, (_, i) => {
    const cat = pick(CATEGORIES, i)
    const center = pick(CENTERS, i)
    const dept = pick(DEPARTMENTS, i)
    const requester = pick(REQUESTERS, i % 4 === 0 ? 0 : i)
    const status = pick(ORDER_STATUSES, i)
    const total = amt(i)
    return {
      id: `OR-${1001 + i}`,
      orCode: `OR-${1001 + i}`,
      createdAt: `2026-06-${String((i % 27) + 1).padStart(2, "0")}`,
      requestDate: `2026-06-${String((i % 27) + 1).padStart(2, "0")}`,
      category: cat.code,
      categoryName: cat.name,
      region: center.region,
      center: center.code,
      centerName: center.name,
      department: dept.code,
      departmentName: dept.name,
      requesterId: requester.id,
      requester: requester.name,
      total,
      status,
      urgency: pick(URGENCIES, i),
      paymentMethod: i % 2 === 0 ? "bank_transfer" : "cash",
      reason: "Operational purchase for the upcoming term.",
      allocationInfo: `${cat.name} budget line — ${center.name}`,
      assignedApproverIsMe: status === "pending" && i % 2 === 0,
      vendorProposals: [
        {
          supplierId: `S-${100 + (i % 6)}`,
          supplier: `Supplier ${String.fromCharCode(65 + (i % 6))}`,
          taxCode: `010${100000 + i}`,
          quote: total,
          note: "Includes delivery & VAT",
          proposed: true,
        },
        {
          supplierId: `S-${200 + (i % 6)}`,
          supplier: `Supplier ${String.fromCharCode(75 + (i % 6))}`,
          taxCode: `030${200000 + i}`,
          quote: Math.round(total * 1.08),
          note: "Faster delivery",
          proposed: false,
        },
      ],
      attachments:
        i % 3 === 0 ? [{ name: "quote.pdf", type: "quote" }] : [],
    }
  })

let ORDERS = buildOrders()

export const listOrders = ({ scope, requesterId, ...filters } = {}) => {
  let out = ORDERS.map((o) => ({ ...o }))
  if (scope === "own") out = out.filter((o) => o.requesterId === (requesterId || "me"))
  if (filters.search) {
    const q = filters.search.toLowerCase()
    out = out.filter(
      (o) =>
        o.orCode.toLowerCase().includes(q) ||
        o.categoryName.toLowerCase().includes(q) ||
        o.requester.toLowerCase().includes(q)
    )
  }
  if (filters.status) out = out.filter((o) => o.status === filters.status)
  if (filters.category) out = out.filter((o) => o.category === filters.category)
  if (filters.region) out = out.filter((o) => o.region === filters.region)
  if (filters.center) out = out.filter((o) => o.center === filters.center)
  if (filters.department) out = out.filter((o) => o.department === filters.department)
  if (filters.awaitingMe === "1" || filters.awaitingMe === true)
    out = out.filter((o) => o.assignedApproverIsMe)
  return out
}

export const getOrder = (id) => {
  const o = ORDERS.find((x) => x.id === id)
  return o ? { ...o } : null
}

export const upsertOrder = (order) => {
  if (order.id && ORDERS.some((o) => o.id === order.id)) {
    ORDERS = ORDERS.map((o) => (o.id === order.id ? { ...o, ...order } : o))
    return getOrder(order.id)
  }
  const id = `OR-${1001 + ORDERS.length}`
  const created = {
    id,
    orCode: id,
    createdAt: new Date().toISOString().slice(0, 10),
    requestDate: new Date().toISOString().slice(0, 10),
    requesterId: "me",
    requester: "You",
    status: order.status || "new",
    vendorProposals: [],
    attachments: [],
    region: "North",
    ...order,
  }
  ORDERS = [created, ...ORDERS]
  return { ...created }
}

export const setOrderStatus = (id, status) => {
  ORDERS = ORDERS.map((o) => (o.id === id ? { ...o, status } : o))
  return getOrder(id)
}

// ── My Orders end-to-end tracking ────────────────────────────────────────────
export const listMyOrders = (filters = {}) => {
  let out = ORDERS.filter((o) => o.requesterId === "me").map((o) => {
    const reached = STAGES.indexOf(stageForStatus(o.status))
    return {
      id: o.id,
      orCode: o.orCode,
      category: o.category,
      categoryName: o.categoryName,
      total: o.total,
      currentStage: stageForStatus(o.status),
      overallStatus: o.status,
      lastUpdated: o.createdAt,
      _stageIdx: reached,
    }
  })
  if (filters.search) {
    const q = filters.search.toLowerCase()
    out = out.filter((o) => o.orCode.toLowerCase().includes(q))
  }
  if (filters.stage) out = out.filter((o) => o.currentStage === filters.stage)
  return out
}

const stageForStatus = (status) => {
  switch (status) {
    case "approved":
      return "purchase"
    case "closed":
      return "payment"
    case "rejected":
    case "new":
    case "pending":
    case "info_requested":
    default:
      return "order"
  }
}

export const getMyOrderTracking = (id) => {
  const o = ORDERS.find((x) => x.id === id)
  if (!o) return null
  const approved = ["approved", "closed"].includes(o.status)
  const closed = o.status === "closed"
  return {
    id: o.id,
    orCode: o.orCode,
    category: o.category,
    categoryName: o.categoryName,
    total: o.total,
    overallStatus: o.status,
    timeline: {
      submittedAt: o.createdAt,
      submittedBy: o.requester,
      approvedBy: approved ? "Center Admin" : null,
      approvedAt: approved ? "2026-06-18" : null,
      poCode: approved ? `PO-${5000 + Number(o.id.slice(3))}` : null,
      purchaseCreatedAt: approved ? "2026-06-19" : null,
      grnCode: closed ? `GRN-${7000 + Number(o.id.slice(3))}` : null,
      goodsReceivedAt: closed ? "2026-06-22" : null,
      paymentStatus: closed ? "paid" : approved ? "unpaid" : null,
      paidAt: closed ? "2026-06-25" : null,
      // Confirm-goods step belongs to this requester only when approved &
      // goods not yet received.
      canConfirmGoods: approved && !closed,
      // Payments are reflected back to the order in batches. A closed order is
      // fully paid across both batches; an approved-but-open order typically has
      // the deposit paid and the balance still pending.
      paymentBatches: approved
        ? [
            {
              label: "Batch 1 — Deposit (30%)",
              amount: Math.round(o.total * 0.3),
              status: "paid",
              at: "2026-06-20",
              ref: `PAY-${8000 + Number(o.id.slice(3))}`,
            },
            {
              label: "Batch 2 — Balance (70%)",
              amount: Math.round(o.total * 0.7),
              status: closed ? "paid" : "unpaid",
              at: closed ? "2026-06-25" : null,
              ref: closed ? `PAY-${8500 + Number(o.id.slice(3))}` : null,
            },
          ]
        : [],
    },
    region: o.region,
    urgency: o.urgency,
    requester: o.requester,
    requestDate: o.requestDate,
  }
}

// ── Requester budget (read-only, by fiscal year) ─────────────────────────────
// One row per category for the given year. Deterministic so the UI is stable;
// the last two categories are intentionally near/below their limit so the
// Budget screen's "remaining low" warning colour is exercised. Years older than
// two back have no data so the empty-state path is reachable.
const REQ_BUDGET_BASE = [
  1_800_000_000, 640_000_000, 1_200_000_000, 900_000_000, 480_000_000,
  760_000_000,
]

export const getRequesterBudget = ({ fiscal_year } = {}) => {
  const currentYear = new Date().getFullYear()
  const year = Number(fiscal_year) || currentYear
  if (year < currentYear - 2 || year > currentYear + 1) return []

  const yearFactor = 1 - (currentYear - year) * 0.08

  return CATEGORIES.map((cat, i) => {
    const total = Math.round(REQ_BUDGET_BASE[i] * yearFactor)
    // Force the last two categories into the low / critical bands.
    const usedRatio =
      i === CATEGORIES.length - 1
        ? 0.94
        : i === CATEGORIES.length - 2
        ? 0.85
        : 0.4 + (i % 4) * 0.09
    const committed = Math.round(total * usedRatio * 0.45)
    const approvedUnpaid = Math.round(total * usedRatio * 0.2)
    const approvedPaid = Math.round(total * usedRatio * 0.35)
    const remaining = total - committed - approvedUnpaid - approvedPaid
    return {
      category: cat.code,
      categoryName: cat.name,
      total,
      committed,
      approvedUnpaid,
      approvedPaid,
      remaining,
    }
  })
}

export const getRequesterCategoryBudget = (categoryId, fiscal_year) => {
  const rows = getRequesterBudget({ fiscal_year })
  return rows.find((r) => r.category === categoryId) || null
}

// ── Suppliers ────────────────────────────────────────────────────────────────
const buildSuppliers = () =>
  Array.from({ length: 14 }, (_, i) => {
    const center = pick(CENTERS, i)
    const cat = pick(CATEGORIES, i)
    const kyc =
      i % 5 === 0 ? "kyc_pending" : i % 5 === 1 ? "kyc_rejected" : "kyc_verified"
    return {
      id: `S-${100 + i}`,
      name: `Supplier ${String.fromCharCode(65 + (i % 26))}${i}`,
      taxCode: `0${1000000000 + i * 137}`,
      region: center.region,
      center: center.code,
      category: cat.code,
      categoryName: cat.name,
      kycStatus: kyc,
      performanceScore: 60 + ((i * 7) % 40),
      active: i % 6 !== 0,
      lastUsed: `2026-0${(i % 6) + 1}-1${i % 9}`,
      pendingApproval: kyc === "kyc_pending",
      proposals: [
        { orCode: `OR-${1001 + i}`, quote: amt(i), proposed: true, date: "2026-05-10" },
        { orCode: `OR-${1005 + i}`, quote: amt(i + 2), proposed: false, date: "2026-04-02" },
      ],
      documents: [
        { name: "business_license.pdf", type: "license" },
        { name: "tax_registration.pdf", type: "tax" },
      ],
    }
  })

let SUPPLIERS = buildSuppliers()

export const listSuppliers = (filters = {}) => {
  let out = SUPPLIERS.map((s) => ({ ...s }))
  if (filters.search) {
    const q = filters.search.toLowerCase()
    out = out.filter(
      (s) => s.name.toLowerCase().includes(q) || s.taxCode.includes(q)
    )
  }
  if (filters.region) out = out.filter((s) => s.region === filters.region)
  if (filters.center) out = out.filter((s) => s.center === filters.center)
  if (filters.category) out = out.filter((s) => s.category === filters.category)
  if (filters.pendingApproval === "1" || filters.pendingApproval === true)
    out = out.filter((s) => s.pendingApproval)
  return out
}

export const getSupplier = (id) => {
  const s = SUPPLIERS.find((x) => x.id === id)
  return s ? { ...s } : null
}

export const setSupplierKyc = (id, kycStatus) => {
  SUPPLIERS = SUPPLIERS.map((s) =>
    s.id === id
      ? { ...s, kycStatus, pendingApproval: kycStatus === "kyc_pending" }
      : s
  )
  return getSupplier(id)
}


// ── Manager budget (region → center breakdown + KPIs) ────────────────────────
const budgetRow = (seed, totalBudget) => {
  const committed = Math.round(totalBudget * (0.35 + (seed % 5) * 0.03))
  const approvedUnpaid = Math.round(totalBudget * (0.12 + (seed % 4) * 0.02))
  const approvedPaid = Math.round(totalBudget * (0.2 + (seed % 6) * 0.02))
  const remaining = totalBudget - committed - approvedUnpaid - approvedPaid
  return { totalBudget, committed, approvedUnpaid, approvedPaid, remaining }
}

export const getManagerBudget = (filters = {}) => {
  const centers = CENTERS.filter(
    (c) =>
      (!filters.region || c.region === filters.region) &&
      (!filters.center || c.code === filters.center)
  )
  const categories = CATEGORIES.filter(
    (c) => !filters.category || c.code === filters.category
  )

  const categoryRows = categories.map((cat, ci) => {
    const base = (3 + (ci % 4)) * 400000000
    const agg = centers.reduce(
      (acc, c, idx) => {
        const r = budgetRow(ci + idx, Math.round(base / Math.max(centers.length, 1)))
        acc.totalBudget += r.totalBudget
        acc.committed += r.committed
        acc.approvedUnpaid += r.approvedUnpaid
        acc.approvedPaid += r.approvedPaid
        acc.remaining += r.remaining
        return acc
      },
      { totalBudget: 0, committed: 0, approvedUnpaid: 0, approvedPaid: 0, remaining: 0 }
    )
    return { categoryName: cat.name, category: cat.code, ...agg }
  })

  const breakdown = centers.map((c, idx) => {
    const r = budgetRow(idx + 2, (4 + (idx % 3)) * 600000000)
    return {
      region: c.region,
      center: c.name,
      centerCode: c.code,
      ...r,
    }
  })

  const kpis = categoryRows.reduce(
    (acc, r) => {
      acc.totalCommitted += r.committed
      acc.unpaid += r.approvedUnpaid
      acc.paid += r.approvedPaid
      acc.remaining += r.remaining
      return acc
    },
    { totalCommitted: 0, unpaid: 0, paid: 0, remaining: 0 }
  )

  return { kpis, categoryRows, breakdown }
}

// ── Report analytics ─────────────────────────────────────────────────────────
export const getReport = (filters = {}) => {
  const { categoryRows, breakdown } = getManagerBudget(filters)

  const spendByCategory = categoryRows.map((r) => ({
    name: r.categoryName,
    value: r.committed + r.approvedUnpaid + r.approvedPaid,
  }))

  const regionAgg = {}
  breakdown.forEach((b) => {
    const v = b.committed + b.approvedUnpaid + b.approvedPaid
    regionAgg[b.region] = (regionAgg[b.region] || 0) + v
  })
  const spendByRegion = Object.entries(regionAgg).map(([name, value]) => ({
    name,
    value,
  }))

  const spendByCenter = breakdown.map((b) => ({
    name: b.center,
    value: b.committed + b.approvedUnpaid + b.approvedPaid,
  }))

  const topSuppliers = SUPPLIERS.slice(0, 6)
    .map((s, i) => ({ name: s.name, spend: amt(i + 3) * 4 }))
    .sort((a, b) => b.spend - a.spend)

  const pendingOrders = ORDERS.filter((o) => o.status === "pending").length

  return {
    spendByCategory,
    spendByRegion,
    spendByCenter,
    budgetVsActual: categoryRows,
    pendingApprovals: { count: pendingOrders, avgCycleDays: 2.4 },
    topSuppliers,
    supplierPerformance: SUPPLIERS.slice(0, 6).map((s) => ({
      name: s.name,
      score: s.performanceScore,
    })),
    savings: { benchmark: 1200000000, actual: 1065000000, savingsPct: 11.25 },
  }
}
